import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentEmployee, selectTaskLocations } from '../reducer';
import { selectSiteTimeZone, selectSiteTotalCarCount } from '../../settings/reducer';
import { Task, TaskCompletion, TaskStatus } from '../models';
import { Interval } from '../models';
import { FancyCheckbox } from '../../../core/components/FancyCheckbox';
import { EmployeeInitialsDropdown } from '../../../core/components/EmployeeInitials';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import UserGroupPermissions, {
  editPermissionRefs,
  deletePermissionRefs,
} from '../../../core/components/UserGroupPermissions';
import { toString } from '../../../core/utils';
import { getTask, saveEssentialTask, saveTask, deleteTask, deleteEssentialTask } from '../actions';
import { getTaskCarCountInterval, getTaskCompletionForecast } from '../helpers';
import { setCurrentEmployee } from '../actions';
import {
  completeTaskCompletion,
  revertTaskCompletion,
  unsnoozeTaskCompletion,
} from '../../overview/actions';
import { useIntl } from 'react-intl';
import { useParams, useHistory } from 'react-router';
import { showDialog } from '../../../core/actions';
import { MdBlock } from 'react-icons/md';
import { IoFlag } from 'react-icons/io5';
import { TaskPriorityTag } from '../../../core/components/TaskPriorityTag';

export enum TaskListDetail {
  None,
  Subtitle,
  Location,
}

interface TaskListItemProps {
  taskCompletion: TaskCompletion;
  detailType?: TaskListDetail;
  displayOptionsMenu?: boolean;
  displayBlockedButton?: boolean;
  displayMaintenanceColumns?: boolean;
  displayTimeInfo?: boolean;
  className?: string;
  toggleCompletion?: any;
  onToggleCompletion?: (args: {
    taskCompletion: TaskCompletion;
    completed: boolean;
    employeeId: number;
  }) => void;
  onToggleSnooze?: () => void;
  onOpen?: (tc: TaskCompletion) => void;
  onEdit?: (task: Task) => void;
  showPriorityTag?: boolean;
  timeRightText?: string;
  additionalLines?: React.ReactNode;
}

export const TaskListItem = ({
  taskCompletion,
  detailType = TaskListDetail.Subtitle,
  displayOptionsMenu = false,
  displayBlockedButton = true,
  displayMaintenanceColumns = false,
  displayTimeInfo = true,
  className,
  toggleCompletion,
  onToggleCompletion,
  onToggleSnooze,
  onOpen,
  onEdit,
  showPriorityTag = false,
  timeRightText,
  additionalLines,
}: TaskListItemProps) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const params = useParams();
  const history = useHistory();
  const { id, status, employeeId, task } = taskCompletion;
  const [completed, setCompleted] = useState(status == Object.keys(TaskStatus)[2]);
  const [snoozed, setSnoozed] = useState(status == Object.keys(TaskStatus)[1]);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [overdue, setOverdue] = useState(false);
  const [time, setTime] = useState('');
  const timezone = useSelector(selectSiteTimeZone);
  const locations = useSelector(selectTaskLocations);
  const currentCarCount = useSelector(selectSiteTotalCarCount);
  const globalEmployee = useSelector(selectCurrentEmployee);
  const [transitioning, setTransitioning] = useState(false);
  const displayEmployee =
    completed && !transitioning ? employeeId ?? globalEmployee : globalEmployee;
  const completionDelay = 500; // This value needs to work with the .transitioning fade in _new-tasks.scss
  const notFoundString = '--';

  const getTaskDetail = () => {
    if (detailType != TaskListDetail.None) {
      let details = null;
      if (detailType == TaskListDetail.Subtitle) {
        details = task.subTitle;
      }
      if (detailType == TaskListDetail.Location) {
        details = getLocation();
      }
      if (details) {
        return <span className="task-description">{details}</span>;
      }
    }
    return null;
  };

  const getLocation = () => {
    const location = locations.find((loc: any) => loc.type === task.locationId);
    return location ? location.message : '';
  };

  const getDueDate = () => {
    if (
      task.interval === toString(Interval, Interval.CarCount) &&
      taskCompletion.scheduleCarCount
    ) {
      if (currentCarCount >= taskCompletion.scheduleCarCount) {
        setOverdue(true);
      } else {
        setOverdue(false);
      }
      return intl.formatMessage(
        { id: 'dueAtCars' },
        { carCount: taskCompletion.scheduleCarCount.toLocaleString('en-US') }
      );
    }
    const rawDue = taskCompletion.scheduleEnd ?? taskCompletion.scheduleStart ?? '';
    const due = timezone ? moment.tz(rawDue, timezone) : moment(rawDue);
    setOverdue(due < moment());
    return intl.formatMessage(
      { id: 'dueBeforeTime' },
      { time: due.add(1, 'days').format('M/D/YYYY') }
    );
  };

  const getDoneDate = () => {
    setOverdue(false);
    if (task.interval === toString(Interval, Interval.CarCount)) {
      if (taskCompletion.carCount) {
        return intl.formatMessage(
          { id: 'doneAtCars' },
          { carCount: taskCompletion.carCount.toLocaleString('en-US') }
        );
      } else {
        return intl.formatMessage({ id: 'taskStatusDone' });
      }
    }
    return intl.formatMessage(
      { id: 'doneAtTime' },
      {
        time: new Date(taskCompletion.timestamp ?? '').toLocaleDateString(undefined, {
          timeZone: timezone ?? undefined,
        }),
      }
    );
  };

  const getDueCarCount = () => {
    if (getTaskCarCountInterval(task)) {
      const forecast = getTaskCompletionForecast(taskCompletion);
      if (forecast) {
        return forecast.carsUntilNextCount
          ? forecast.carsUntilNextCount.toLocaleString('en-US')
          : notFoundString;
      }
    }
    return notFoundString;
  };

  const getDueCarCountDays = () => {
    if (getTaskCarCountInterval(task)) {
      const forecast = getTaskCompletionForecast(taskCompletion);
      if (forecast && forecast.dlyCarAvg > 0) {
        const days = Math.round(forecast.carsUntilNextCount / forecast.dlyCarAvg);
        return days ?? notFoundString;
      }
    }
    return notFoundString;
  };

  const getCarCountInterval = () => {
    const interval = getTaskCarCountInterval(task);
    if (interval) {
      return interval.toLocaleString('en-US');
    }
    return notFoundString;
  };

  const getTimeString = () => {
    return completed ? getDoneDate() : getDueDate();
  };

  const handleEmployeeSelect = (empId: number) => {
    dispatch(setCurrentEmployee(empId));
  };

  const completeTask = () => {
    if (onToggleCompletion) {
      onToggleCompletion({ taskCompletion, completed, employeeId: displayEmployee as number });
      if (toggleCompletion) toggleCompletion(id, displayEmployee);
      setCompleted(!completed);
    } else {
      if (!transitioning) {
        setTransitioning(true);
        setCompleted(!completed);
      }
    }
  };

  const snoozeTask = () => {
    if (!snoozed) {
      dispatch(
        showDialog('SNOOZE_TASK', {
          taskCompletion: taskCompletion,
          onSnooze: onToggleSnooze,
          dispatch: dispatch,
        })
      );
    } else {
      dispatch(
        unsnoozeTaskCompletion({
          employeeId: displayEmployee,
          taskIds: [id],
          notes: null,
        })
      );
      setSnoozed(false);
    }
  };

  const showTaskDetails = () => {
    dispatch(
      showDialog('VIEW_TASK_DETAILS', {
        title: intl.formatMessage({ id: 'taskDetails' }),
        taskCompletion: taskCompletion,
        match: { params: params },
        history: history,
        dispatch: dispatch,
      })
    );
  };

  const editDialogProps = {
    title: 'Edit Task',
    onSubmit: (editedTask: Task) => {
      return task?.groupId
        ? dispatch(
            saveEssentialTask({
              task: editedTask,
            })
          )
        : dispatch(
            saveTask({
              task: editedTask,
            })
          );
    },
    location: location,
    isNew: false,
    initialTask: task,
    isEssentialTask: task?.groupId ? true : false,
    modelYears: task?.modelYears,
  };

  const handleEditTask = () => {
    if (onEdit) {
      onEdit(task);
    } else {
      dispatch(getTask(task.id));
      dispatch(showDialog('UPSERT_TASK', editDialogProps));
    }
  };

  const handleDeleteTask = () => {
    const { name, groupId } = task;
    const taskId = task?.id;
    const isEssentialTask = groupId ? true : false;
    const deletionParams = {
      taskId,
    };
    const confirmProps = {
      title: intl.formatMessage({ id: 'archiveTask' }),
      text: intl.formatMessage({ id: 'taskArchiveConfirmation' }, { name }),
      onReady: isEssentialTask
        ? () => dispatch(deleteEssentialTask(deletionParams))
        : () => dispatch(deleteTask(deletionParams)),
    };
    dispatch(showDialog('CONFIRM_DIALOG', confirmProps));
  };

  useEffect(() => {
    setTime(getTimeString());
  }, [id, completed, employeeId]);

  useEffect(() => {
    let timeout: any;
    if (transitioning) {
      timeout = setTimeout(() => {
        if (completed) {
          dispatch(
            completeTaskCompletion({
              employeeId: globalEmployee,
              taskIds: [id],
              notes: {},
            })
          );
        } else {
          dispatch(
            revertTaskCompletion({
              employeeId: globalEmployee,
              taskIds: [id],
              notes: null,
            })
          );
        }
        if (toggleCompletion) toggleCompletion(id, globalEmployee);
        setTransitioning(false);
      }, completionDelay);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [completed]);

  useEffect(() => {
    if (id && status) {
      setCompleted(status == Object.keys(TaskStatus)[2]);
      setSnoozed(status == Object.keys(TaskStatus)[1]);
      setTransitioning(false);
    }
  }, [id, status]);

  return (
    <div
      className={`task${className ? ' ' + className : ''}${completed ? ' completed' : ''}${
        transitioning ? ' transitioning' : ''
      }`}
    >
      <div>
        {!snoozed ? (
          <FancyCheckbox
            isChecked={completed}
            onClick={(e: any) => {
              completeTask();
            }}
            size={30}
            title={
              completed
                ? intl.formatMessage({ id: 'uncompleteTaskTitleText' })
                : intl.formatMessage({ id: 'completeTaskTitleText' })
            }
          />
        ) : (
          <div className="task-snoozed no-select" role="presentation">
            <MdBlock />
          </div>
        )}
        <EmployeeInitialsDropdown
          selectable={!completed}
          selectedEmployee={displayEmployee}
          onEmployeeSelect={handleEmployeeSelect}
          listHeight={150}
        />
        <button
          className="task-details"
          title={intl.formatMessage({ id: 'viewTaskDetails' })}
          onClick={() => {
            if (onOpen) {
              onOpen(taskCompletion);
            } else {
              showTaskDetails();
            }
          }}
        >
          <span className="task-name">{task.name}</span>
          {getTaskDetail()}
          {additionalLines && <div className="task-extra-lines">{additionalLines}</div>}
        </button>
      </div>
      <div className={`details ${displayMaintenanceColumns ? ' maintenance-details' : ''}`}>
        {!completed && displayBlockedButton && !displayMaintenanceColumns && (
          <button
            className={`button snooze-button${snoozed ? ' snoozed' : ''}`}
            title={
              snoozed ? intl.formatMessage({ id: 'unblock' }) : intl.formatMessage({ id: 'block' })
            }
            onClick={snoozeTask}
            disabled={completed}
          >
            <IoFlag />
          </button>
        )}
        {displayMaintenanceColumns && (
          <div className="maintenance-columns">
            <span>{getDueCarCount()}</span>
            <span>{getCarCountInterval()}</span>
            <span>{getDueCarCountDays()}</span>
          </div>
        )}
        {showPriorityTag && <TaskPriorityTag task={task} />}
        {displayOptionsMenu && (
          <Dropdown
            className="task-options-dropdown"
            isOpen={optionsOpen}
            toggle={() => setOptionsOpen(!optionsOpen)}
          >
            <DropdownToggle className="task-options">
              <i className="uil uil-ellipsis-v"></i>
            </DropdownToggle>
            <DropdownMenu className="dropdown-menu-right" right>
              <UserGroupPermissions itemRef={editPermissionRefs.task}>
                <>
                  <DropdownItem onClick={handleEditTask}>
                    {intl.formatMessage({ id: 'edit' })}
                  </DropdownItem>
                </>
              </UserGroupPermissions>
              <UserGroupPermissions itemRef={deletePermissionRefs.task}>
                <>
                  <DropdownItem divider />
                  <DropdownItem onClick={handleDeleteTask}>
                    {intl.formatMessage({ id: 'delete' })}
                  </DropdownItem>
                </>
              </UserGroupPermissions>
            </DropdownMenu>
          </Dropdown>
        )}
      </div>
      {displayTimeInfo && (
        <div className={`task-time task-time-row${overdue ? ' overdue' : ''}`}>
          <span>{time}</span>
          {timeRightText && <span className="task-time-right">{timeRightText}</span>}
        </div>
      )}
    </div>
  );
};
